<template>
  <div class="subscribe-form" data-testid="subscribe-form">
    <box type="primary" :title="t('Escolha uma franquia e um plano')">
      <franchise-select
        elem-id="subscribe-franquia"
        :model-value="selectedFranquiaId ?? undefined"
        :label="t('Franquia')"
        :include-ids="eligibleFranquiaIds"
        @update:model-value="selectedFranquiaId = Number($event)"
      />
      <select-list
        elem-id="subscribe-plan"
        data-testid="subscribe-plan"
        :itens-select="planItems"
        :auto-select-first="false"
        v-model="selectedPlanId"
        :label="t('Plano')"
      />
      <!-- Gateway picker: shown only when multiple gateways exist for the selected plan -->
      <select-list
        v-if="showGatewayPicker"
        elem-id="subscribe-gateway"
        data-testid="subscribe-gateway"
        :itens-select="gatewayItems"
        :auto-select-first="false"
        v-model="selectedGateway"
        :label="t('Forma de pagamento')"
      />
      <!-- Auto-selected gateway notice: shown when only one gateway is available -->
      <div
        v-if="autoSelectedGateway !== null"
        data-testid="subscribe-gateway-auto"
        class="subscribe-gateway-auto"
      >
        {{ t('Forma de pagamento') }}: <strong>{{ autoSelectedGateway }}</strong>
      </div>
      <!-- The modal was opened bound to a franchise the picker would not have
           offered. Say which gate refused it instead of clearing the field
           silently — an empty picker with no explanation reads as a bug. -->
      <div
        v-if="preselectionRefusal !== null"
        data-testid="subscribe-preselection-refused"
        class="subscribe-pais-sem-mercado"
      >
        <i class="pi pi-info-circle" />
        {{ preselectionRefusal }}
      </div>
      <!-- D6: named empty-catalog reason — the franchise's country has no billing market -->
      <div
        v-if="emptyReason === 'pais_sem_mercado'"
        data-testid="subscribe-pais-sem-mercado"
        class="subscribe-pais-sem-mercado"
      >
        <i class="pi pi-info-circle" />
        {{ t('O país desta franquia ainda não possui mercado de assinatura configurado') }}
      </div>
    </box>

    <!-- Signing is the LAST step: you agree to terms whose price you have
         already seen. The block hides itself when there is nothing to sign. -->
    <subscription-terms-consent
      ref="consentRef"
      :franquia-id="selectedFranquiaId"
      :plan-id="selectedPlanId != null ? Number(selectedPlanId) : null"
      @update:ready="termsReady = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../eventBus'
import { log } from '../../utils/DebugLogger'
import { useSubscribe } from '../../composables/useSubscribe'
import { useMySubscriptions } from '../../composables/useMySubscriptions'
import { isSetupSatisfied } from '../../utils/setupFeeStatus'
import { hasLiveSubscription } from '../../utils/subscriptionLiveStatus'
import { currencyFactor } from '../../utils/currencyMinorUnits'

import SubscriptionTermsConsent from './SubscriptionTermsConsent.vue'

const props = defineProps<{
  franquiaId?: number
}>()

const consentRef = ref<InstanceType<typeof SubscriptionTermsConsent> | null>(null)
const termsReady = ref(false)

const { t, n } = useI18n()
const { plans, emptyReason, listAvailablePlans, subscribe, redirectToCheckout } = useSubscribe()
const { rows: mineRows, fetchMySubscriptions } = useMySubscriptions()

// THE shared predicate (src/utils/setupFeeStatus) — never a local copy.
/**
 * Which franchises this modal may offer.
 *
 * TWO gates, both mirroring a backend refusal:
 *   setup fee satisfied — POST /subscriptions/subscribe answers setup_fee_owed.
 *   not already live    — it answers 'Franquia já possui assinatura ativa'.
 *
 * The second one was missing: the picker listed a franchise that already had
 * an active subscription, so the user could walk the whole modal and only then
 * be refused (reported 2026-08-20). Offering an action the backend is certain
 * to reject is worse than not offering it.
 */
const eligibleFranquiaIds = computed(() =>
  mineRows.value
    .filter((r) => isSetupSatisfied(r))
    .filter((r) => !hasLiveSubscription(mineRows.value, r.franchise_id))
    .map((r) => r.franchise_id),
)

const selectedFranquiaId = ref<number | null>(props.franquiaId ?? null)
const selectedPlanId = ref<number | string | null>(null)
const selectedGateway = ref<string | null>(null)
const preselectionRefusal = ref<string | null>(null)

// Pre-fill franchise when parent supplies one (e.g. opened from a row)
watch(
  () => props.franquiaId,
  (id) => {
    if (id != null) selectedFranquiaId.value = id
  },
)

/**
 * The preselection has to pass the SAME two gates the picker applies.
 *
 * `franquia_id` arrives on the modal.subscribe.open payload and used to be
 * written straight into selectedFranquiaId, bypassing eligibleFranquiaIds
 * entirely — so a surface that offered 'Assinar' when it should not have
 * produced a fully-populated form that the backend then refused with
 * 'Franquia já possui assinatura ativa' (reported 2026-08-21). Every calling
 * surface being gated is not enough on its own: this is the last point where
 * the rule can still be enforced before the request leaves.
 *
 * Guarded on mineRows being loaded — the rows arrive async, and before they do
 * EVERY franchise looks ineligible, which would wipe a legitimate preselection.
 */
watch([() => mineRows.value, selectedFranquiaId], () => {
  if (mineRows.value.length === 0) return
  // Nothing selected: leave any standing notice alone. Clearing it here would
  // erase the very message the previous pass wrote, since clearing the
  // selection is how this guard refuses one.
  if (selectedFranquiaId.value == null) return

  const id = Number(selectedFranquiaId.value)
  const row = mineRows.value.find((r) => Number(r.franchise_id) === id)
  if (row === undefined || eligibleFranquiaIds.value.includes(id)) {
    preselectionRefusal.value = null
    return
  }
  preselectionRefusal.value = hasLiveSubscription(mineRows.value, id)
    ? t('Esta franquia já possui uma assinatura ativa.')
    : t('A taxa de implantação desta franquia precisa ser quitada antes de assinar.')
  selectedFranquiaId.value = null
}, { immediate: true })

// When the user picks a different franchise, re-fetch plans scoped to that
// franchise's market currency and reset the plan selection (market rows may differ).
watch(selectedFranquiaId, async (id) => {
  selectedPlanId.value = null
  selectedGateway.value = null
  try {
    await listAvailablePlans(id ?? undefined)
  } catch (err) {
    log('SubscribeForm', 'watch:selectedFranquiaId', 'Erro ao recarregar planos', err)
  }
})

// Reset gateway selection when the plan changes.
watch(selectedPlanId, () => {
  selectedGateway.value = null
})

/** Map a currency code to a BCP-47 locale for n() formatting. */
const CURRENCY_LOCALE: Record<string, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  CLP: 'es-CL',
}

/**
 * Deduplicated plan items for the plan picker.
 * Multiple rows may share the same plan_id (one per gateway); we show the first
 * row per plan_id so the user picks the plan, then picks the gateway separately.
 */
const planItems = computed(() => {
  const seen = new Set<number>()
  const items: Array<{ ID: number; Name: string }> = []
  for (const plan of plans.value) {
    if (seen.has(plan.id)) continue
    seen.add(plan.id)
    const currency = plan.currency ?? 'BRL'
    const locale = CURRENCY_LOCALE[currency] ?? 'pt-BR'
    const factor = currencyFactor(locale, currency)
    const majorValue = (plan.price_cents ?? 0) / factor
    const formatted = n(majorValue, { style: 'currency', currency, currencyDisplay: 'symbol' })
    items.push({
      ID: plan.id,
      Name: `${plan.name} — ${formatted} / ${plan.cycle}`,
    })
  }
  return items
})

/**
 * All rows for the selected plan_id (multiple when multiple gateways offer it).
 */
const selectedPlanRows = computed(() =>
  plans.value.filter((p) => p.id === Number(selectedPlanId.value)),
)

/**
 * Gateway options derived from the selected plan's rows.
 * Each entry maps provider_code → display label.
 */
const gatewayItems = computed(() =>
  selectedPlanRows.value
    .filter((r) => r.provider_code)
    .map((r) => ({
      ID: r.provider_code as string,
      Name: r.provider_code as string,
    })),
)

/**
 * Show the gateway picker only when there are multiple gateway options.
 */
const showGatewayPicker = computed(
  () => selectedPlanId.value != null && gatewayItems.value.length > 1,
)

/**
 * When exactly one gateway is available for the selected plan, it is auto-selected
 * and we show a read-only notice (no picker).
 * When no plan is selected or multiple gateways exist, this is null.
 */
const autoSelectedGateway = computed<string | null>(() => {
  if (selectedPlanId.value == null) return null
  if (gatewayItems.value.length !== 1) return null
  return gatewayItems.value[0].ID
})

/**
 * The resolved provider_code: auto-selected when exactly one option,
 * otherwise the user's picker selection.
 */
const resolvedProviderCode = computed<string | null>(() => {
  if (autoSelectedGateway.value !== null) return autoSelectedGateway.value
  return selectedGateway.value
})

/** Both franchise AND plan must be selected, plus a gateway resolved, before submitting. */
const canSave = computed(
  () =>
    selectedFranquiaId.value != null &&
    selectedPlanId.value != null &&
    resolvedProviderCode.value != null &&
    // Unsigned terms would be refused by subscribe() with
    // 'termos_nao_aceitos'; disabling the button says so before the round trip.
    termsReady.value,
)

async function handleSubmit(): Promise<void> {
  if (!selectedFranquiaId.value || !selectedPlanId.value || !resolvedProviderCode.value) {
    eventBus.emit('globalMessage.error', t('Selecione uma franquia, um plano e uma forma de pagamento antes de assinar.'))
    return
  }

  const franquiaId = selectedFranquiaId.value
  const planId = Number(selectedPlanId.value)
  const providerCode = resolvedProviderCode.value

  // Sign FIRST: the backend gate refuses subscribe() while the live agreement
  // is unsigned, so signing after would guarantee the refusal.
  const signed = await consentRef.value?.sign()
  if (signed === false) {
    return
  }

  try {
    const dados = await subscribe({ franquia_id: franquiaId, plan_id: planId, provider_code: providerCode })
    eventBus.emit('subscribe.redirecting', { franquia_id: franquiaId, plan_id: planId, checkout_url: dados.checkout_url })
    redirectToCheckout(dados.checkout_url)
  } catch (err) {
    log('SubscribeForm', 'handleSubmit', 'Erro ao assinar plano', err)

    // Mostre o MOTIVO. O texto fixo anterior descartava a mensagem que o
    // composable já propaga (ele usa throwOnEnvelopeFailure justamente para
    // que a recusa vire um erro com nome), então uma causa acionável —
    // "a franquia 50 não tem CPF/CNPJ cadastrado" — chegava ao usuário como
    // "Tente novamente", que é conselho impossível de seguir: repetir não
    // preenche um documento que falta.
    const reason = (err as Error)?.message?.trim()
    eventBus.emit(
      'globalMessage.error',
      reason ? reason : t('Erro ao iniciar assinatura. Tente novamente.'),
    )
  }
}

onMounted(async () => {
  try {
    if (mineRows.value.length === 0) {
      await fetchMySubscriptions()
    }
    await listAvailablePlans(props.franquiaId)
  } catch (err) {
    log('SubscribeForm', 'onMounted', 'Erro ao carregar planos', err)
  }
})

defineExpose({ handleSubmit, canSave })
</script>
